import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { uniqueIds } from "@/lib/supabase-joins";
import { buildEdgeFunctionErrorMessage } from "@/lib/edgeFunctionErrors";

type EscrowPaymentRow = Database["public"]["Tables"]["escrow_payments"]["Row"];
type EscrowAllocationRow = Database["public"]["Tables"]["escrow_allocations"]["Row"];
type EscrowConfirmationRow = Database["public"]["Tables"]["escrow_confirmations"]["Row"];
type MatterRow = Database["public"]["Tables"]["matters"]["Row"];

export type EscrowRecipientInput = {
  recipient_type: "party" | "external";
  recipient_role?: string | null;
  recipient_user_id?: string | null;
  recipient_name: string;
  amount: number;
};

export interface EscrowPaymentWithDetails extends EscrowPaymentRow {
  matter: Pick<MatterRow, "id" | "matter_number" | "title" | "property_address" | "status" | "current_stage" | "property_value" | "contract_value"> | null;
  escrow_allocations: EscrowAllocationRow[];
  escrow_confirmations: EscrowConfirmationRow[];
}

export interface CreateEscrowPaymentInput {
  escrow_payment_id?: string;
  matter_id?: string;
  title?: string;
  description?: string;
  total_amount?: number;
  allocations?: EscrowRecipientInput[];
  callback_url: string;
}

export function useEscrowPayments() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["escrow_payments", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("escrow_payments")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const payments = data || [];
      const paymentIds = payments.map((payment) => payment.id);
      const matterIds = uniqueIds(payments.map((payment) => payment.matter_id));

      const [allocationsResult, confirmationsResult, mattersResult] = await Promise.all([
        paymentIds.length > 0
          ? supabase.from("escrow_allocations").select("*").in("escrow_payment_id", paymentIds)
          : Promise.resolve({ data: [], error: null }),
        paymentIds.length > 0
          ? supabase.from("escrow_confirmations").select("*").in("escrow_payment_id", paymentIds)
          : Promise.resolve({ data: [], error: null }),
        matterIds.length > 0
          ? supabase
            .from("matters")
            .select("id, matter_number, title, property_address, status, current_stage, property_value, contract_value")
            .in("id", matterIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (allocationsResult.error) throw allocationsResult.error;
      if (confirmationsResult.error) throw confirmationsResult.error;
      if (mattersResult.error) throw mattersResult.error;

      const allocationsByPaymentId = new Map<string, EscrowAllocationRow[]>();
      for (const allocation of allocationsResult.data || []) {
        const current = allocationsByPaymentId.get(allocation.escrow_payment_id) || [];
        current.push(allocation);
        allocationsByPaymentId.set(allocation.escrow_payment_id, current);
      }

      const confirmationsByPaymentId = new Map<string, EscrowConfirmationRow[]>();
      for (const confirmation of confirmationsResult.data || []) {
        const current = confirmationsByPaymentId.get(confirmation.escrow_payment_id) || [];
        current.push(confirmation);
        confirmationsByPaymentId.set(confirmation.escrow_payment_id, current);
      }

      const matterMap = new Map((mattersResult.data || []).map((matter) => [matter.id, matter]));

      const detailedPayments = payments as unknown as EscrowPaymentWithDetails[];
      return detailedPayments.map((payment) => ({
        ...payment,
        matter: payment.matter_id ? matterMap.get(payment.matter_id) || null : null,
        escrow_allocations: [...(allocationsByPaymentId.get(payment.id) || [])].sort((left, right) =>
          new Date(left.created_at).getTime() - new Date(right.created_at).getTime(),
        ),
        escrow_confirmations: [...(confirmationsByPaymentId.get(payment.id) || [])].sort((left, right) =>
          new Date(left.confirmed_at).getTime() - new Date(right.confirmed_at).getTime(),
        ),
      }));
    },
  });
}

export function useCreateEscrowPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateEscrowPaymentInput) => {
      const { data, error } = await supabase.functions.invoke("create-escrow-payment", {
        body: payload,
      });

      if (error) {
        throw new Error(buildEdgeFunctionErrorMessage(error, "We could not start escrow checkout right now."));
      }
      return data as {
        escrow_payment_id: string;
        authorization_url: string;
        reference: string;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["escrow_payments"] });
    },
  });
}

export function useConfirmEscrowRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (escrow_payment_id: string) => {
      const { data, error } = await supabase.functions.invoke("confirm-escrow-release", {
        body: { escrow_payment_id },
      });

      if (error) throw error;
      return data as {
        success: boolean;
        status: string;
        confirmed_count: number;
        required_count: number;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["escrow_payments"] });
    },
  });
}
